// src/pages/Annonces/AnnonceCard.jsx
import React, { useState, useMemo } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { deleteAnnonce } from "./../../services/annoncesServices";
import { useUser } from "./../../contexts/UserContext";
import ButtonEdit from "./../../components/Button/ButtonEdit";
import ButtonDelete from "./../../components/Button/ButtonDelete";
import ButtonTel from "../../components/Button/ButtonTel";
import ButtonMail from "../../components/Button/ButtonMail";
import EditAnnonceModal from "./EditAnnonceModal";
import { toast } from "react-toastify";

const DEFAULT_IMAGE =
  "https://res.cloudinary.com/dw2x1voyb/image/upload/v1721468236/illustration_petite_annonce_cnvse7.webp";

const categoryBadgeClass = (category = "") => {
  const c = category.toLowerCase();
  if (c.includes("recherche enseignant")) return "bg-amber-500";
  if (c.includes("enseignant cherche club")) return "bg-sky-500";
  if (c.includes("recherche matériel")) return "bg-indigo-500";
  if (c.includes("don matériel")) return "bg-emerald-500";
  return "bg-slate-500";
};

const AnnonceCard = ({ annonce, fetchAnnonces }) => {
  const { user } = useUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const localInfo = useMemo(() => {
    try {
      const raw = localStorage.getItem("userInfo");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const role_id = user?.role_id ?? localInfo.role_id;
  const userId = user?.userId ?? localInfo.userId;
  const canModify =
    role_id === 1 || role_id === 2 || userId === annonce.user_id;

  const imageUrl = annonce.image_url || DEFAULT_IMAGE;
  const isInactive = !annonce.is_active;

  const handleDeleteAnnonce = async () => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await deleteAnnonce(annonce.id);
      toast.success("Annonce supprimée.");
      fetchAnnonces();
    } catch (error) {
      console.error("Erreur lors de la suppression de l'annonce:", error);
      toast.error("Erreur lors de la suppression de l'annonce.");
    }
  };

  const handleEdit = () => setIsEditModalOpen(true);
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    fetchAnnonces();
  };

  return (
    <>
      <article
        className={`rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-900/90 transition transform hover:-translate-y-0.5 hover:shadow-xl flex flex-col ${
          isInactive ? "opacity-70 grayscale" : ""
        }`}
      >
        {/* IMAGE EN HAUT (toutes tailles) */}
        <div className="relative w-full h-44 sm:h-52">
          <img
            src={imageUrl}
            alt={annonce.title}
            className="w-full h-full object-cover"
          />
          {/* Catégorie */}
          <div
            className={`absolute top-3 left-3 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold text-white shadow-md ${categoryBadgeClass(
              annonce.category
            )}`}
          >
            {annonce.category}
          </div>
          {/* Statut active / masquée */}
          <div className="absolute bottom-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold bg-black/60 text-white">
            {isInactive ? "Annonce masquée" : "Annonce active"}
          </div>
        </div>

        {/* CONTENU TEXTE EN DESSOUS */}
        <div className="flex-1 flex flex-col justify-between p-4 md:p-5 space-y-3">
          <div>
            <h3 className="text-base sm:text-lg font-bold uppercase text-slate-900 dark:text-slate-50 font-montserrat">
              {annonce.title}
            </h3>

            <p className="mt-1 text-[11px] sm:text-xs text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800/80 rounded-lg px-3 py-1.5">
              {annonce.user?.prenom} {annonce.user?.nom} –{" "}
              {annonce.club_user || "Club non renseigné"}
            </p>

            {annonce.content && (
              <div
                className="mt-2 text-xs sm:text-sm text-slate-700 dark:text-slate-200 prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: annonce.content }}
              />
            )}

            <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
              Publié le{" "}
              {new Date(annonce.date_annonce).toLocaleDateString("fr-FR", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          {/* Bas de carte : contacts + actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <div className="flex flex-wrap gap-2">
              {annonce.tel_user && <ButtonTel tel={annonce.tel_user} />}
              {annonce.mail_user && <ButtonMail mail={annonce.mail_user} />}
            </div>

            {canModify && (
              <div className="flex gap-2 ml-auto">
                {/* Tu peux aussi remplacer par <ButtonEdit /> / <ButtonDelete /> si tu préfères */}
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-sm shadow-sm transition"
                  title="Modifier l'annonce"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={handleDeleteAnnonce}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm shadow-sm transition"
                  title="Supprimer l'annonce"
                >
                  <FaTrashAlt />
                </button>
              </div>
            )}
          </div>
        </div>
      </article>

      {isEditModalOpen && (
        <EditAnnonceModal
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          fetchAnnonces={fetchAnnonces}
          annonce={annonce}
        />
      )}
    </>
  );
};

export default AnnonceCard;
